import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { Theme } from "@mui/material/styles"
import Image from "next/image"
import { sharedColors } from "ui/theme/colors"
import { Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { Team } from "./TeamTabs"

interface BackCardProps {
  el: Team
  isFlipped?: boolean
}

const StyledFlipCard = styled.div<{ $isFlipped?: boolean; theme?: Theme }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 3;
  padding: 24px;
  gap: 16px;
  border: 1px solid ${sharedColors.borderGray};
  border-radius: 12px;
  position: absolute;
  top: 0;
  left: 0;
  background-color: white;
  opacity: 0;
  transition: opacity 0.5s ease;
  pointer-events: none;

  ${({ theme }) => theme?.breakpoints.up("md")} {
    *:hover > & {
      opacity: 1;
      pointer-events: auto;
    }
  }

  ${({ theme, $isFlipped }: { theme?: Theme; $isFlipped?: boolean }) => `
    ${theme?.breakpoints.down("md")} {
      ${
        $isFlipped
          ? `
            opacity: 1;
            pointer-events: auto;
          `
          : ``
      }
    }
  `}
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`

const StyledTextContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${sharedColors.borderGray};
`

const BottomBar = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  text-decoration: none;
  p {
    &:hover {
      box-shadow: inset 0 -1px 0 0 black;
    }
  }
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`

export default function BackCard({ el, isFlipped }: BackCardProps) {
  return (
    <StyledFlipCard $isFlipped={isFlipped}>
      <TopBar>
        <Image
          src={getUmbracoMediaUrl(el.teamThumbnailImage || el.teamMainImage)}
          alt={el.teamName}
          width={84}
          height={84}
          style={{ borderRadius: "8px", objectFit: "cover" }}
        />
        <TextContainer>
          <Typography variant="h4">{el.teamName}</Typography>
          <Typography variant="body1">{el.teamRole}</Typography>
        </TextContainer>
      </TopBar>
      <StyledTextContent>
        <StyledTypography
          variant="body2"
          dangerouslySetInnerHTML={{ __html: el.teamDescription }}
        />
      </StyledTextContent>
      <Divider />
      {el?.linkedinLink && (
        <BottomBar href={el.linkedinLink}>
          <Image
            src={"/icons/linkedin.svg"}
            alt="linkedin"
            width={26.5}
            height={26.5}
          />
          <Typography variant="body2">חפשו אותי בלינקדיאין</Typography>
        </BottomBar>
      )}
    </StyledFlipCard>
  )
}
